import type { RCard } from 'v2/types/api/billing'
import { Currency } from '../billing.config'
import { CardState } from '../types'

/**
 * Context the eligibility rules need, sourced from the account/balance queries.
 * `accountLoaded` is separate from `currency` on purpose — see `isAutopayCard`.
 */
export interface CardEligibilityContext {
  /** The account details query resolved WITH data. */
  accountLoaded: boolean
  /** The account or balance query is actively in flight. */
  queriesLoading: boolean
  /** `accountDetails.account.currency`. */
  currency?: string
  /** `balance.auto_recharge`. */
  autoRecharge?: boolean
}

/**
 * Is this the card that silently backs auto-recharge?
 *
 * Deleting it stops auto-recharge, so it must go through `DeleteCardDialog`,
 * which offers a replacement and says what will happen — never the plain delete.
 *
 * Mirrors the predicate that used to live in `payment-modes/index.tsx`:
 * `isUSOrg && card.default && !!balance?.auto_recharge`, where
 * `isUSOrg = !!accountDetails && accountDetails.account?.currency !== INR`.
 * The `accountLoaded` flag preserves that first clause exactly — a loaded account
 * with no currency counted as US, and only an UNLOADED account counted as
 * not-US. Collapsing the two into `currency !== 'INR'` would flip the
 * still-loading case from false to true.
 */
export const isAutopayCard = (card: RCard, ctx: CardEligibilityContext): boolean =>
  ctx.accountLoaded && ctx.currency !== Currency.INR && !!card.default && !!ctx.autoRecharge

/**
 * May this card be made the billing default?
 *
 * Only an ACTIVE card that is not already the default. An expired or
 * authentication-required card cannot be charged, so promoting it would point
 * billing at a card that will fail — the old `PaymentCard` control rendered
 * "Make Default" only for `card.state === ACTIVE`, and that gate has to survive
 * the move into the overflow menu.
 */
export const canMakeDefault = (card: RCard): boolean =>
  card.state === CardState.ACTIVE && !card.default

/**
 * Can we decide safely yet?
 *
 * Gates on the queries being IN FLIGHT, not on data being present — which is what
 * `isQueriesLoading` did on the Payment methods page. The distinction matters:
 * `useAccountDetailsQuery` is `enabled: !!accountId`, so before the profile
 * resolves the query is disabled and reports `isLoading: false` with no data.
 * Gating on data would leave Remove permanently disabled in that state; gating on
 * loading reproduces the previous behaviour exactly (the autopay check then reads
 * `accountLoaded: false` and simply doesn't fire).
 */
export const canDecideDelete = (ctx: CardEligibilityContext): boolean => !ctx.queriesLoading
